import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCrossTabCount } from 'hooks/useCrossTabCount';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { EBroadcastChannel, EFeatureFlag } from 'constants/enums';
import { OfferData } from 'constants/apiResponses.types';

import {
  clearShowing,
  getClosedOfferIdsThisSession,
  getCooldownEndsAtMilliseconds,
  isAnotherTabShowing,
  markDismissed,
  markOfferClosedThisSession,
  pickEligibleStoreRefreshOffer,
  writeShowingLock,
} from './useStoreRefresh.utils';
import { SHOWING_STALE_MILLISECONDS } from './useStoreRefresh.consts';

interface IUseStoreRefreshProps {
  offers: OfferData[];
}

interface IUseStoreRefreshReturn {
  count: number;
  eligibleOffer: OfferData | undefined;
  isOpen: boolean;
  /** Milliseconds when cooldown ends per offer, only when that offer is on cooldown. */
  cooldownEndsByOfferId: Record<string, number>;
  onCollect: (offerId: string) => void;
  onClose: (offerId: string) => void;
}

/**
 * Hook that manages store-refresh popup eligibility, cross-tab locking,
 * and dismiss handling for one or more store-refresh offers.
 *
 * @example
 * const {
 *   eligibleOffer,
 *   isOpen,
 *   cooldownEndsByOfferId,
 *   onCollect,
 *   onClose,
 * } = useStoreRefresh({ offers: storeRefreshOffers });
 */
export const useStoreRefresh = (
  props: IUseStoreRefreshProps,
): IUseStoreRefreshReturn => {
  const { offers } = props;

  const isEnabled = useFeatureFlag(EFeatureFlag.POPUP_STORE_REFRESH_ENABLED);
  const { count, reset } = useCrossTabCount({
    channelName: EBroadcastChannel.STORE_REFRESH_CHANNEL_NAME,
    storageKey: EBroadcastChannel.STORE_REFRESH_STORAGE_KEY,
    autoIncrement: isEnabled && offers.length > 0,
  });

  useEffect((): (() => void) => {
    return (): void => {
      clearShowing();
    };
  }, []);

  const eligibleOffer = useMemo(
    (): OfferData | undefined =>
      isEnabled ? pickEligibleStoreRefreshOffer(offers, count) : undefined,
    [isEnabled, offers, count],
  );

  const [closedIds, setClosedIds] = useState<ReadonlySet<string>>(
    getClosedOfferIdsThisSession,
  );

  const isOpen = useMemo((): boolean => {
    if (!isEnabled || !eligibleOffer) return false;
    if (closedIds.has(eligibleOffer.offerId)) return false;
    return !isAnotherTabShowing();
  }, [isEnabled, eligibleOffer, closedIds]);

  useEffect((): (() => void) | undefined => {
    if (!isOpen) return undefined;
    writeShowingLock();
    const interval = setInterval(writeShowingLock, SHOWING_STALE_MILLISECONDS / 2);
    return (): void => clearInterval(interval);
  }, [isOpen]);

  const cooldownEndsByOfferId = useMemo((): Record<string, number> => {
    if (!isEnabled) return {};
    const map: Record<string, number> = {};
    for (const offer of offers) {
      const endsAt = getCooldownEndsAtMilliseconds(offer);
      if (endsAt !== null) {
        map[offer.offerId] = endsAt;
      }
    }
    return map;
  }, [isEnabled, offers, count]);

  const onCollect = useCallback((offerId: string): void => {
    markDismissed(offerId);
    clearShowing();
    reset();
  }, [reset]);

  const onClose = useCallback((offerId: string): void => {
    setClosedIds(markOfferClosedThisSession(offerId));
    clearShowing();
  }, []);

  return {
    count,
    eligibleOffer,
    isOpen,
    cooldownEndsByOfferId,
    onCollect,
    onClose,
  };
};
